import React, { FC, useState } from 'react';
import {
  Box,
  Heading,
  SideSheet,
  SideSheetBody,
  SideSheetClose,
  SideSheetHeader,
  SideSheetSubHeader,
  Text,
  ToggleSwitch,
} from '@bechtel/gds';
import { useEnvProvider, ProjectAppTypes, WeldMilestoneType } from '@bechtel/client-common';
import { CommodityMilestone, CommodityStatusCode, MilestoneStatusCode, Weld } from '@bechtel/types';
import { useCommodity } from '~/detail/commodities/context';
import { useWorkPackage } from '~/detail/context';
import { t, Trans } from '@lingui/macro';
import { WeldAssignReassign } from '~/components/organisms/assign-reassign-weld';
import { useWorkPackagePermissions } from '~/hooks';
import { WeldTagStyled } from './styled';
import { find, some } from 'lodash';

interface AssignReassignWeldSidesheetProps {
  weld: Weld;
  isReassign: boolean;
  close: () => void;
}

export const AssignReassignWeldSidesheet: FC<AssignReassignWeldSidesheetProps> = ({ weld, isReassign, close }) => {
  const [isFitupRequired, setIsFitupRequired] = useState<boolean>(weld?.fitupRequest?.isFitupRequired ?? true);
  const wp = useWorkPackage();
  const { envConfig } = useEnvProvider();
  const { assignedCrewMembers } = wp;
  const commodity = useCommodity();
  const { canCreateFitupRequest } = useWorkPackagePermissions(assignedCrewMembers);
  const fitupMilestone = WeldMilestoneType.FITUP;
  let hasFitupMilestone = some(weld?.milestones, (item) => {
    return item.name.toUpperCase() === fitupMilestone && item.currentStatus.codeI18n !== MilestoneStatusCode.CANCELED;
  });

  const canShowToggle = () => {
    const hasFitupMilestoneUpdated = some(weld?.milestones, (item: CommodityMilestone) => {
      if (
        item.name.toUpperCase() === fitupMilestone &&
        item.currentStatus.codeI18n == MilestoneStatusCode.REJECTED &&
        weld?.status?.status === CommodityStatusCode.SENT_BACK_TO_PIPING
      ) {
        hasFitupMilestone = false;
        return false;
      } else {
        return (
          item.name.toUpperCase() === fitupMilestone && item.currentStatus.codeI18n !== MilestoneStatusCode.CANCELED
        );
      }
    });
    const installMilestone: CommodityMilestone | undefined = find(
      weld?.milestones,
      (item) =>
        item?.currentStatus?.codeI18n == MilestoneStatusCode.PLANNED ||
        item?.currentStatus?.codeI18n == MilestoneStatusCode.UNPLANNED
    );
    if (installMilestone?.id && !hasFitupMilestoneUpdated) {
      return true;
    } else {
      return false;
    }
  };

  return (
    <SideSheet visible onDismiss={close} data-testid="assign-weld-sidesheet">
      <SideSheetHeader title={isReassign ? t`Reassign Welding Foreman` : t`Assign Welding Foreman`}>
        <SideSheetClose onClick={close}></SideSheetClose>
      </SideSheetHeader>

      <SideSheetSubHeader>
        <Box mb={{ sm: '400' }}>
          <Heading>
            <Trans>WorkPackage</Trans>:&nbsp;
          </Heading>
          {wp.tag}
        </Box>
        {!canShowToggle() && (
          <Box>
            <Heading>{commodity.typeName}:&nbsp;</Heading>
            {commodity.tag}
          </Box>
        )}
      </SideSheetSubHeader>

      <SideSheetBody>
        <Box flexGrow={{ sm: 1 }} flexDirection={{ sm: 'column' }}>
          {canShowToggle() && envConfig.appName === ProjectAppTypes.PF && (
            <WeldTagStyled>
              <Text size="400" weight="600" color="grey.1000" family="graphik">
                {weld?.tag}
              </Text>
              {canCreateFitupRequest && (
                <Box>
                  <ToggleSwitch
                    disabled={hasFitupMilestone}
                    isToggled={isFitupRequired}
                    onToggle={() => {
                      setIsFitupRequired(!isFitupRequired);
                    }}
                  />
                  <Box pl={{ sm: '450' }}>
                    <Text size="300" color="grey.1000">
                      <Trans>Fitup</Trans>
                    </Text>
                  </Box>
                </Box>
              )}
            </WeldTagStyled>
          )}
          <WeldAssignReassign
            weldIds={[weld?.id]}
            fitupRequest={weld?.fitupRequest}
            isFitupRequired={isFitupRequired}
            isReassign={isReassign}
            close={close}
            isFlangeSidesheet={false}
            weld={weld}
          />
        </Box>
      </SideSheetBody>
    </SideSheet>
  );
};
